import { useMemo } from 'react';
import { useAchievements } from '@busy/api';
import { useT } from '../../lib/i18n';
import { nicknameHistory, type NicknameJourneyEntry } from '../../lib/nickname';
import { nicknameArtworkUrl } from '../../lib/badgeArtwork';

// Nickname Journey list — every nickname the barber has held over
// time, in the order they were first reached. The current nickname
// pins at the top and is labeled CURRENT; everything below it is past.
//
// Now embedded INSIDE the NicknameHero on the Achievements page rather
// than rendered as a standalone collapsible section. The hero owns
// the open/closed state and uses <NicknameJourneyList> below as the
// expandable content. No more dispatch event, no more cross-component
// scroll handoff — one component, one toggle.
//
// History is reconstructed from achievement earned_at timestamps via
// nicknameHistory() in lib/nickname — there's no separate DB table,
// the timeline is derived purely from the achievements list.

export function NicknameJourneyList() {
  const t = useT();
  const all = useAchievements();
  const earned = all.data ?? [];

  const journey = useMemo<NicknameJourneyEntry[]>(() => nicknameHistory(earned), [earned]);
  // Newest first — the current title sits at the top of the list.
  const ordered = useMemo(() => [...journey].reverse(), [journey]);
  const currentKey = ordered[0]?.key;

  if (ordered.length === 0) {
    return (
      <p className="text-[12px] text-white/70 italic">
        {t('nickname.journey.empty')}
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {ordered.map((entry) => (
        <li key={entry.key}>
          <JourneyRow entry={entry} isCurrent={entry.key === currentKey} t={t} />
        </li>
      ))}
    </ul>
  );
}

function JourneyRow({
  entry,
  isCurrent,
  t,
}: {
  entry: NicknameJourneyEntry;
  isCurrent: boolean;
  t: ReturnType<typeof useT>;
}) {
  const artwork = nicknameArtworkUrl(entry.key);
  const title = t(entry.key);
  const earnedAtLabel = entry.firstEarnedAt
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'long' }).format(
        new Date(entry.firstEarnedAt),
      )
    : t('nickname.journey.startingTitle');

  return (
    <div
      className={`flex items-center gap-3 rounded-2xl border p-3 transition-colors ${
        isCurrent
          ? 'border-gold-500 bg-gold-500/8 dark:bg-gold-500/10'
          : 'border-line bg-surface'
      }`}
    >
      {/* Badge thumbnail — uses the artwork when present, falls back to a
          navy/gold mini-hero with a crown for tiers without art yet. */}
      <div className="shrink-0 w-16 h-16 rounded-xl overflow-hidden border border-line bg-navy-900 grid place-items-center">
        {artwork ? (
          <img src={artwork} alt="" className="w-full h-full object-cover" />
        ) : (
          <div
            className="w-full h-full grid place-items-center text-gold-400"
            style={{
              background:
                'radial-gradient(ellipse at 70% 30%, rgba(212,169,66,.30), transparent 60%), linear-gradient(135deg, #0d1424 0%, #1a2954 100%)',
            }}
          >
            <CrownIcon className="w-6 h-6" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <strong className="font-display text-base leading-tight truncate">{title}</strong>
          {isCurrent && (
            <span className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-gold-500 shrink-0">
              {t('nickname.journey.current')}
            </span>
          )}
        </div>
        <div className="text-[12px] text-ink-500 mt-0.5">
          {entry.firstEarnedAt
            ? t('nickname.journey.earnedOn', { date: earnedAtLabel })
            : earnedAtLabel}
        </div>
      </div>
    </div>
  );
}

function CrownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2 4l4 7 6-9 6 9 4-7-2 16H4z" />
      <path d="M5 20h14" />
    </svg>
  );
}
